import { useState, useEffect } from 'react'
import { Building2, Plus, Loader2 } from 'lucide-react'
import { useAccountsStore } from '@/store/accountsStore'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AccountType } from '@/types/account'

// Create a simplified Account type that matches what we're using
interface SimplifiedAccount {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  currency?: string;
  color?: string;
  isArchived?: boolean;
}

// Helper for formatting currency amounts
const formatAmount = (amount: number, currency?: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

export function AccountSummary() {
  const { user } = useAuth()
  const { accounts, loading, error, fetchAccounts } = useAccountsStore()
  
  // Filter only savings accounts
  const savingsAccounts = accounts.filter(account => account.type === 'savings' && !account.isArchived)
  
  useEffect(() => {
    if (user) {
      fetchAccounts(user.id)
    }
  }, [user, fetchAccounts])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-8 h-8 animate-spin text-secondary dark:text-primary-dark" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center p-6 bg-negative/10 dark:bg-negative-dark/10 rounded-xl">
        <p className="text-negative dark:text-negative-dark">Failed to load savings accounts</p>
        <Button 
          variant="outline" 
          className="mt-4 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-border dark:border-border-dark rounded-xl shadow-sm" 
          onClick={() => user && fetchAccounts(user.id)}
        >
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
          Savings Accounts
        </h2>
        <Button 
          className="mt-2 sm:mt-0 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 rounded-xl shadow-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Savings Account
        </Button>
      </div>

      {savingsAccounts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {savingsAccounts.map((account) => (
            <SavingsAccountCard key={account.id} account={account as SimplifiedAccount} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
          <div className="w-16 h-16 rounded-xl bg-secondary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-secondary dark:text-primary-dark" />
          </div>
          <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
            No savings accounts yet
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2 max-w-md mx-auto">
            Create a savings account to start tracking your financial goals
          </p>
          <Button
            className="mt-6 bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Savings Account
          </Button>
        </div>
      )}
    </div>
  )
}

function SavingsAccountCard({ account }: { account: SimplifiedAccount }) {
  return (
    <Card className="group overflow-hidden border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 dark:from-primary-dark/20 dark:to-primary-dark/10 flex items-center justify-center shadow-inner">
                <Building2 className="w-6 h-6 text-secondary dark:text-primary-dark" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary truncate">
                  {account.name}
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-0.5">
                  {account.currency || 'USD'} • Savings Account
                </p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Balance
            </p>
            <p className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
              {formatAmount(account.balance, account.currency)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 